// Pilot sign-in (#323): magic-link auth, invite-code activation, per-user
// flags. A self-contained controller + useSyncExternalStore hook (the
// wakeLock pattern), deliberately OUTSIDE the zustand store: the session is
// held by supabase-js in its own localStorage key with its own refresh
// lifecycle, and mirroring it into persisted app state would create a second
// source of truth that drifts.
//
// Shape of the machine:
//   unconfigured — no Supabase env at build time: the feature does not exist
//   loading      — client booting / consuming a magic-link redirect
//   signedOut    — configured, no session
//   linkSent     — magic link emailed; waiting for the user to open it
//   needsCode    — signed in without access yet (the closed beta): the app
//                  treats this exactly like signed-out (identity only)
//   active       — signed in with access; flags and batch are loaded
//
// The Supabase client is imported DYNAMICALLY on first use so the main
// bundle pays nothing when auth is unconfigured, and created with the
// implicit flow: PKCE breaks when the emailed link opens in a different
// browser than the one that requested it (Gmail's in-app browser is the
// classic case), and students clicking a link from a mail app is exactly
// this feature's happy path.

import { useSyncExternalStore } from 'react';
import { authConfig, type AuthConfig } from './config';

export type AuthStatus =
  | 'unconfigured'
  | 'loading'
  | 'signedOut'
  | 'linkSent'
  | 'needsCode'
  | 'active';

/** Power, and nothing else (docs/access-model.md): 'admin' is the owner's
 * staff flag. Access to the members' features is separate (`has_access`). */
export type PilotRole = 'user' | 'admin';

export interface AuthState {
  status: AuthStatus;
  /** The signed-in address (or the address a link was just sent to). */
  email: string | null;
  /** The name on the account (user_metadata.full_name), shown in place of a
   * hidden Apple address; null when none is known. */
  name: string | null;
  /** The signed-in user's id, the profile's key; null while signed out. */
  userId: string | null;
  /** Per-user flags from the pilot profile row; {} unless active. */
  flags: Record<string, unknown>;
  /** The invite code's batch label; null unless active. */
  batch: string | null;
  /** The profile's role; null while signed out. */
  role: PilotRole | null;
  /** Last failed action's plain-English reason; cleared by the next action. */
  error: string | null;
  /** An action is in flight (send link / activate / sign out). */
  busy: boolean;
}

interface ProfileRow {
  flags: Record<string, unknown> | null;
  batch: string | null;
  role?: string | null;
  has_access?: boolean | null;
}

/** The role a profile row carries, read the same way by the session and the
 * admin panel. */
export function profileRole(row: { role?: unknown }): PilotRole {
  return row.role === 'admin' ? 'admin' : 'user';
}

/** Does the profile let this user into the members' features? `has_access`
 * since migration 0011; before it, the role said so (student, tutor, admin),
 * and before roles at all, a batch meant a redeemed code. Reading all three
 * keeps the app working whichever side of a migration the database is on. */
export function profileHasAccess(row: {
  role?: unknown;
  has_access?: unknown;
  batch?: unknown;
}): boolean {
  if (row.role === 'admin') return true;
  if (typeof row.has_access === 'boolean') return row.has_access;
  if (row.role === 'student' || row.role === 'tutor') return true;
  if (row.role === 'member' || row.role === 'user') return false;
  return !!row.batch;
}

/** The part of a Supabase session this module reads. */
interface AuthSession {
  user: { id: string; email?: string; user_metadata?: Record<string, unknown> };
}

/** A name the provider handed us at this sign-in (Apple's, first time only),
 * read before the stored copy lands in the session. */
let justGivenName: string | null = null;

/** The account's name as Supabase keeps it: full_name (Google and an Apple
 * name we stored), else name. */
function nameOf(session: AuthSession): string | null {
  if (justGivenName) return justGivenName;
  const meta = session.user.user_metadata ?? {};
  for (const key of ['full_name', 'name']) {
    const value = meta[key];
    if (typeof value === 'string' && value.trim()) return value.trim();
  }
  return null;
}

/**
 * The minimal structural slice of the Supabase client this module uses.
 * Tests inject a fake through __setAuthTestHooks; the real client satisfies
 * it at runtime (asserted once at the creation site).
 */
/** Providers whose own sign-in UI hands us an ID token. */
export type IdTokenProvider = 'google' | 'apple';

export interface AuthClient {
  auth: {
    getSession(): Promise<{ data: { session: AuthSession | null } }>;
    onAuthStateChange(callback: (event: string, session: AuthSession | null) => void): {
      data: { subscription: { unsubscribe: () => void } };
    };
    signInWithOtp(options: {
      email: string;
      options?: { emailRedirectTo?: string };
    }): Promise<{ error: { message: string } | null }>;
    /** Optional so pre-Google test fakes stand unchanged; the real client
     * always has it (#370). */
    signInWithOAuth?(options: {
      provider: 'google';
      options?: { redirectTo?: string };
    }): Promise<{ error: { message: string } | null }>;
    /** Optional for the same reason: Google's own button hands us an ID
     * token, exchanged here. */
    signInWithIdToken?(options: {
      provider: IdTokenProvider;
      token: string;
      nonce?: string;
    }): Promise<{ error: { message: string } | null }>;
    /** Optional so older test fakes stand: stores Apple's name, which Apple
     * sends once and the ID token never carries. */
    updateUser?(attributes: {
      data: Record<string, unknown>;
    }): Promise<{ error: { message: string } | null }>;
    signOut(): Promise<{ error: { message: string } | null }>;
  };
  from(table: string): {
    select(columns: string): {
      eq(
        column: string,
        value: string,
      ): {
        maybeSingle(): Promise<{ data: ProfileRow | null; error: { message: string } | null }>;
      };
    };
  };
  rpc(
    fn: string,
    args: Record<string, unknown>,
  ): Promise<{ data: ProfileRow | null; error: { message: string } | null }>;
}

const SIGNED_OUT: AuthState = {
  status: 'signedOut',
  email: null,
  name: null,
  userId: null,
  flags: {},
  batch: null,
  role: null,
  error: null,
  busy: false,
};

let state: AuthState = { ...SIGNED_OUT, status: 'unconfigured' };
const listeners = new Set<() => void>();

let client: AuthClient | null = null;
let initPromise: Promise<void> | null = null;
let unsubscribe: (() => void) | null = null;

// Test seams: a fake client skips the dynamic import; a config override lets
// tests exercise the unconfigured branch regardless of the build env.
let testClient: AuthClient | null = null;
let testConfig: AuthConfig | null | undefined;

function emit(next: Partial<AuthState>) {
  state = { ...state, ...next };
  for (const listener of listeners) listener();
}

function resolvedConfig(): AuthConfig | null {
  return testConfig !== undefined ? testConfig : authConfig();
}

async function createRealClient(config: AuthConfig): Promise<AuthClient> {
  const { createClient } = await import('@supabase/supabase-js');
  return createClient(config.url, config.anonKey, {
    auth: {
      persistSession: true,
      autoRefreshToken: true,
      detectSessionInUrl: true,
      flowType: 'implicit',
    },
  }) as unknown as AuthClient;
}

// --- the last profile this device confirmed ---------------------------------
//
// Offline, the profile cannot be read, and without it a signed-in member would
// boot as if signed out — and since playlists follow the account, their
// playlists would vanish exactly when the device has no network. So the last
// profile that granted access is remembered for its user, and a failed read
// falls back to it. It is presentation, never a lock: the server still
// refuses anything the profile no longer allows.

const PROFILE_CACHE = 'vlc:auth-profile';

interface CachedProfile {
  userId: string;
  flags: Record<string, unknown>;
  batch: string | null;
  role: PilotRole | null;
}

function readCachedProfile(userId: string): CachedProfile | null {
  try {
    const raw = JSON.parse(localStorage.getItem(PROFILE_CACHE) ?? 'null') as unknown;
    if (!raw || typeof raw !== 'object') return null;
    const p = raw as Record<string, unknown>;
    if (p.userId !== userId) return null;
    return {
      userId,
      flags: p.flags && typeof p.flags === 'object' ? (p.flags as Record<string, unknown>) : {},
      batch: typeof p.batch === 'string' ? p.batch : null,
      role: p.role === 'admin' ? 'admin' : p.role === 'user' ? 'user' : null,
    };
  } catch {
    return null;
  }
}

function writeCachedProfile(profile: CachedProfile | null) {
  try {
    if (profile) localStorage.setItem(PROFILE_CACHE, JSON.stringify(profile));
    else localStorage.removeItem(PROFILE_CACHE);
  } catch {
    // Storage refused (private window): offline boots just lose the fallback.
  }
}

/** Signed in: what does the profile grant? A missing profile or one without
 * access both land on needsCode — the members' features key off 'active'.
 * A profile that cannot be read falls back to the last one this device
 * confirmed for this user.
 *
 * The query names the user's own row. RLS alone does not narrow it to one:
 * "admins read profiles" is a second permissive policy, so an admin's
 * unfiltered select returns every profile, and maybeSingle turns that into
 * an error the moment anyone else has signed in. */
async function loadProfile(session: AuthSession) {
  if (!client) return;
  const email = session.user.email ?? null;
  const name = nameOf(session);
  const userId = session.user.id;
  const { data, error } = await client
    .from('pilot_profiles')
    // Every column, not a list: has_access only exists from migration 0011,
    // and naming it would fail against a database that has not run it.
    .select('*')
    .eq('user_id', userId)
    .maybeSingle();
  if (error) {
    const cached = readCachedProfile(userId);
    if (cached) {
      emit({
        status: 'active',
        email,
        name,
        userId,
        flags: cached.flags,
        batch: cached.batch,
        role: cached.role,
        error: null,
        busy: false,
      });
      return;
    }
    emit({
      status: 'needsCode',
      email,
      name,
      userId,
      flags: {},
      batch: null,
      role: null,
      error: error.message,
      busy: false,
    });
    return;
  }
  const role = data ? profileRole(data) : null;
  if (!data || !profileHasAccess(data)) {
    writeCachedProfile(null);
    emit({
      status: 'needsCode',
      email,
      name,
      userId,
      flags: {},
      batch: null,
      role,
      error: null,
      busy: false,
    });
    return;
  }
  writeCachedProfile({ userId, flags: data.flags ?? {}, batch: data.batch, role });
  emit({
    status: 'active',
    email,
    name,
    userId,
    flags: data.flags ?? {},
    batch: data.batch,
    role,
    error: null,
    busy: false,
  });
}

async function init(): Promise<void> {
  const config = resolvedConfig();
  if (!config && !testClient) {
    if (state.status !== 'unconfigured') emit({ ...SIGNED_OUT, status: 'unconfigured' });
    return;
  }
  emit({ status: 'loading' });
  try {
    client = testClient ?? (await createRealClient(config as AuthConfig));
  } catch {
    // A failed client load must never take the app down — auth simply
    // does not exist this session.
    emit({ ...SIGNED_OUT, status: 'unconfigured' });
    return;
  }
  const sub = client.auth.onAuthStateChange((event, session) => {
    if (event === 'SIGNED_OUT' || !session) {
      if (event === 'SIGNED_OUT') writeCachedProfile(null);
      emit({ ...SIGNED_OUT });
      return;
    }
    if (event === 'SIGNED_IN' || event === 'INITIAL_SESSION' || event === 'TOKEN_REFRESHED') {
      // Re-check the profile only when we are not already active as this
      // user — a token refresh must not flash the UI through needsCode, but
      // a sign-in as someone else (another tab) must not keep the last
      // user's role, flags and id.
      if (state.status !== 'active' || state.userId !== session.user.id) void loadProfile(session);
      else emit({ email: session.user.email ?? state.email, name: nameOf(session) ?? state.name });
    }
  });
  unsubscribe = () => sub.data.subscription.unsubscribe();
  const { data } = await client.auth.getSession();
  if (data.session) await loadProfile(data.session);
  else emit({ ...SIGNED_OUT });
}

function ensureInit() {
  if (!initPromise) initPromise = init();
  return initPromise;
}

/**
 * Boot the client explicitly (idempotent). The first mounted useAuth() also
 * boots, but an explicit boot is what consumes a magic-link redirect even if
 * no auth UI has mounted yet.
 */
export function bootAuth(): Promise<void> {
  return ensureInit();
}

/** Has the client been asked to boot? Before that, a configured build's
 * 'unconfigured' state only means the check has not started. */
export function authConfigured(): boolean {
  return resolvedConfig() !== null || testClient !== null;
}

export function authStarted(): boolean {
  return initPromise !== null;
}

/** Does this device remember a profile that granted access? Lets a signed-in
 * member's reload show the music while the sign-in check runs. */
export function hasCachedAccess(): boolean {
  try {
    return localStorage.getItem(PROFILE_CACHE) !== null;
  } catch {
    return false;
  }
}

export async function sendMagicLink(email: string): Promise<void> {
  await ensureInit();
  if (!client) return;
  const address = email.trim();
  if (!address) return;
  emit({ busy: true, error: null });
  const { error } = await client.auth.signInWithOtp({
    email: address,
    // The FULL current URL, not the origin: a student signing in from a
    // shared-setlist link (#325) must land back ON that link, or the sign-in
    // succeeds and the setlist they came for is gone. The redirect allowlist
    // covers paths via its /** wildcards.
    options: { emailRedirectTo: window.location.href },
  });
  if (error) emit({ busy: false, error: error.message });
  else emit({ status: 'linkSent', email: address, busy: false, error: null });
}

/** Google sign-in (#370): a full-page round trip through Supabase's
 * authorize endpoint. Same redirect rule as the magic link — the FULL
 * current URL, so a student arriving on a shared-setlist link lands back on
 * it signed in. On success the browser navigates away, so `busy` simply
 * holds until the page unloads; an error (popup rules, provider off) lands
 * back here and reports. */
export async function signInWithGoogle(): Promise<void> {
  await ensureInit();
  if (!client) return;
  if (!client.auth.signInWithOAuth) {
    emit({ error: 'Google sign-in is not available in this build.' });
    return;
  }
  emit({ busy: true, error: null });
  const { error } = await client.auth.signInWithOAuth({
    provider: 'google',
    options: { redirectTo: window.location.href },
  });
  if (error) emit({ busy: false, error: error.message });
}

/** Sign-in through a provider's own UI on this site (Google's button, Apple's
 * popup): it hands back an ID token, exchanged for a session here with no page
 * navigation, so the provider's consent screen names this site rather than the
 * Supabase project's domain. `nonce` is the raw value whose SHA-256 the
 * provider was given. */
export async function signInWithIdToken(
  provider: IdTokenProvider,
  token: string,
  nonce?: string,
  fullName?: string | null,
): Promise<void> {
  await ensureInit();
  if (!client) return;
  if (!client.auth.signInWithIdToken) {
    emit({ error: 'This sign-in is not available in this build.' });
    return;
  }
  const given = fullName?.trim() || null;
  justGivenName = given;
  emit({ busy: true, error: null });
  const { error } = await client.auth.signInWithIdToken({ provider, token, nonce });
  if (error) {
    justGivenName = null;
    emit({ busy: false, error: error.message });
    return;
  }
  if (given) {
    emit({ name: given });
    // Keep it on the account: Apple never sends the name again. A failure
    // costs only the name on other devices.
    await client.auth.updateUser?.({ data: { full_name: given } }).catch(() => undefined);
  }
  justGivenName = null;
  emit({ busy: false, error: null });
}

export async function redeemCode(code: string): Promise<void> {
  await ensureInit();
  if (!client) return;
  const trimmed = code.trim();
  if (!trimmed) return;
  emit({ busy: true, error: null });
  const { data, error } = await client.rpc('redeem_invite_code', { invite_code: trimmed });
  if (error || !data) {
    emit({ busy: false, error: error?.message ?? 'That code was not accepted.' });
    return;
  }
  if (state.userId) {
    writeCachedProfile({
      userId: state.userId,
      flags: data.flags ?? {},
      batch: data.batch,
      role: profileRole(data),
    });
  }
  emit({
    status: 'active',
    flags: data.flags ?? {},
    batch: data.batch,
    role: profileRole(data),
    busy: false,
    error: null,
  });
}

export async function signOut(): Promise<void> {
  await ensureInit();
  if (!client) return;
  emit({ busy: true, error: null });
  const { error } = await client.auth.signOut();
  // The SIGNED_OUT auth event also lands here; emitting directly keeps the
  // UI honest even if the event is delayed.
  if (error) emit({ busy: false, error: error.message });
  else emit({ ...SIGNED_OUT });
}

/** Give up on the sent link and go back to the email form. */
export function resetToSignedOut(): void {
  if (state.status === 'linkSent') emit({ ...SIGNED_OUT });
}

function subscribe(listener: () => void): () => void {
  listeners.add(listener);
  // First subscriber boots the client — which is also what consumes a
  // magic-link redirect's hash tokens on page load.
  void ensureInit();
  return () => listeners.delete(listener);
}

function getSnapshot(): AuthState {
  return state;
}

/** Non-React read (the command layer's flag checks, tests). */
export function getAuthState(): AuthState {
  return state;
}

export function useAuth(): AuthState {
  return useSyncExternalStore(subscribe, getSnapshot);
}

/** Non-React subscription (usage analytics watches for activation). */
export function subscribeAuth(listener: () => void): () => void {
  return subscribe(listener);
}

/**
 * The underlying supabase-js client, untyped — for modules whose needs are
 * outside AuthClient's minimal auth surface (the events writer inserts rows).
 * Null until the client has booted or when auth is unconfigured.
 */
export function getSupabaseRaw(): unknown | null {
  return client;
}

/** Per-user flags for feature gating (#327); {} unless the user is active. */
export function useAuthFlags(): Record<string, unknown> {
  return useAuth().flags;
}

// ---------------------------------------------------------------------------
// Test seams

export function __setAuthTestHooks(hooks: {
  client?: AuthClient | null;
  config?: AuthConfig | null;
}): void {
  if ('client' in hooks) testClient = hooks.client ?? null;
  if ('config' in hooks) testConfig = hooks.config;
}

export function __resetAuthForTests(): void {
  unsubscribe?.();
  unsubscribe = null;
  client = null;
  initPromise = null;
  testClient = null;
  testConfig = undefined;
  state = { ...SIGNED_OUT, status: 'unconfigured' };
  listeners.clear();
  writeCachedProfile(null);
}
